import {
  useState, useEffect, useRef, useMemo, useContext
} from 'react';
import {
  useLazyDataModel,
  extend,
  string as stringType,
  number as numberType,
  bool as boolType,
  shape as shapeType,
  arrayOf as arrayType,
  customType,
  params,
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { BATCH_SIZE } from '../constants';
import { mergeAiQuantities } from '../proPlanningToolHelpers';

export const useQuantityEstimator = ({
  intentGroups,
  resolvedIntentItemMap,
  listTitle,
  aiPromptMessage,
  questionnaireAnswers,
  metaData,
  enabled,
}) => {
  const { deliveryZip } = useContext(ExperienceContext);
  const { storeId, storeZip, membershipInformation } = useStore({ varnish: true });

  const [itemIdsToFetch, setItemIdsToFetch] = useState([]);
  const [batchPage, setBatchPage] = useState(0);
  const [resolvedProductData, setResolvedProductData] = useState({});
  const [isFetchingProducts, setIsFetchingProducts] = useState(false);
  const [estimatedMaterials, setEstimatedMaterials] = useState(null);
  const [estimationError, setEstimationError] = useState(null);
  const [isDone, setIsDone] = useState(false);
  const [groupsTotal, setGroupsTotal] = useState(0);
  const [groupsDone, setGroupsDone] = useState(0);

  const incrementGroupsDone = () => setGroupsDone((prev) => prev + 1);

  const estimateQuantityFired = useRef(false);
  const started = useRef(false);

  const loyaltyMembershipInput = useMemo(() => {
    return membershipInformation?.data?.loyaltyMembership || null;
  }, [membershipInformation?.data?.loyaltyMembership]);

  const mcvisId = useMemo(() => {
    if (typeof window !== 'undefined') {
      return window._aape?.ECID || null;
    }
    return null;
  }, []);

  const [fetchProducts, {
    data: productsData, error: productsError
  }] = useLazyDataModel('products', {
    variables: {
      loyaltyMembershipInput,
      isBrandPricingPolicyCompliant: true,
      storeId,
      zipCode: deliveryZip || storeZip
    },
    ssr: false,
  });

  const [estimateQuantity] = useLazyDataModel('interactWithAgent', {
    variables: {},
    context: { withAuth: true }
  });

  // Start estimation when enabled becomes true
  useEffect(() => {
    if (!enabled || started.current) return;
    if (!resolvedIntentItemMap || !intentGroups?.length) return;

    started.current = true;

    // Collect item IDs for materials that need AI quantity estimation
    const aiEligibleItemIds = intentGroups
      .flatMap((group) => group.materials || [])
      .filter((material) => material?.estimateAiQuantity === true && material?.isSelected !== false)
      .flatMap((material) => resolvedIntentItemMap[material.intent]?.itemIds || [])
      .filter(Boolean);

    const uniqueItemIds = [...new Set(aiEligibleItemIds)];

    if (uniqueItemIds.length === 0) {
      estimateQuantityFired.current = true;
      setEstimatedMaterials(intentGroups);
      setIsDone(true);
      return;
    }

    setResolvedProductData({});
    setItemIdsToFetch(uniqueItemIds);
    estimateQuantityFired.current = false;
    setBatchPage(1);
    setIsFetchingProducts(true);
  }, [enabled, resolvedIntentItemMap, intentGroups]);

  // Fire-and-forget: this Apollo version's query exec function returns no promise, so the
  // result only arrives via `productsData`/`productsError` below (mirrors useProductBatchLoader.js).
  useEffect(() => {
    if (batchPage < 1 || itemIdsToFetch.length === 0) return;

    const startIndex = (batchPage - 1) * BATCH_SIZE;
    const batch = itemIdsToFetch.slice(startIndex, startIndex + BATCH_SIZE);

    if (batch.length > 0) {
      fetchProducts({ variables: { itemIds: batch } });
    }
  }, [batchPage, itemIdsToFetch, fetchProducts]);

  // Keyed only on productsData/productsError so this fires on Apollo's own state changes,
  // not whenever the fetch effect re-runs. A failed/empty batch just means these items don't
  // get product data - never blocks the flow.
  useEffect(() => {
    if (!isFetchingProducts) return;
    if (!productsData && !productsError) return;

    const products = productsData?.products;
    if (products?.length > 0) {
      setResolvedProductData((prev) => {
        const updated = { ...prev };
        products.forEach((product) => {
          if (product?.itemId) {
            updated[product.itemId] = product;
          }
        });
        return updated;
      });
    }

    const nextStartIndex = batchPage * BATCH_SIZE;
    if (nextStartIndex < itemIdsToFetch.length) {
      setBatchPage((prev) => prev + 1);
    } else {
      setIsFetchingProducts(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsData, productsError]);

  // Enrich intentGroups and fire estimateQuantity once all products are resolved.
  //
  // `itemIdsToFetch.length === 0` guards a same-commit race: on the render where `enabled`
  // first flips true, this effect can run before the start-estimation effect's setState
  // calls take effect (refs update immediately, state doesn't) - without it, this fires
  // with an empty `resolvedProductData` on every single run.
  useEffect(() => {
    if (estimateQuantityFired.current) return;
    if (!started.current || isFetchingProducts || itemIdsToFetch.length === 0 || !intentGroups?.length) return;

    // Materials with no resolved itemId are excluded (nothing to estimate); materials with
    // an itemId but no productData are still forwarded for a best-effort estimate.
    const enrichedGroups = intentGroups.map((group) => ({
      ...group,
      materials: (group.materials || [])
        .filter((material) => material?.isSelected !== false && material?.estimateAiQuantity === true)
        .map((material) => {
          const itemId = resolvedIntentItemMap[material.intent]?.itemIds?.[0];
          if (!itemId) return null;

          const productData = resolvedProductData[itemId];
          if (productData) {
            return {
              ...material,
              itemId,
              info: productData.info,
              pricing: productData.pricing,
              specificationGroup: productData.specificationGroup,
              identifiers: productData.identifiers
            };
          }
          return { ...material, itemId };
        })
        .filter(Boolean),
    })).filter((group) => group.materials.length > 0);

    if (!enrichedGroups.length) {
      estimateQuantityFired.current = true;
      setEstimatedMaterials(intentGroups);
      setIsDone(true);
      return;
    }

    estimateQuantityFired.current = true;
    setGroupsTotal(enrichedGroups.length);
    setGroupsDone(0);

    const { refType, refId, channel } = metaData || {};

    const sharedVariables = {
      interactionType: 'QUANTITY_ESTIMATION',
      agent: 'DEFAULT',
      template: 'QUANTITY_ESTIMATION',
      message: aiPromptMessage || '',
      metaData: {
        refType: refType || 'LIST',
        refId: refId || '',
        channel: channel || '',
        mcvis: mcvisId || ''
      }
    };

    const estimateGroup = (group) => {
      const templateDataItems = [
        { key: 'resolvedMaterialsGroup', value: JSON.stringify([group]) },
        { key: 'title', value: listTitle || '' }
      ];

      if (questionnaireAnswers) {
        templateDataItems.push({ key: 'questionnaireAnswers', value: questionnaireAnswers });
      }

      return estimateQuantity({
        variables: {
          ...sharedVariables,
          templateData: templateDataItems
        }
      }).then((response) => {
        incrementGroupsDone();
        const { materialsGroups } = response?.data?.interactWithAgent || {};
        return materialsGroups || [];
      }).catch((err) => {
        incrementGroupsDone();
        throw err;
      });
    };

    // Fire one mutation per enriched group in parallel
    const groupPromises = enrichedGroups.map(estimateGroup);

    Promise.allSettled(groupPromises)
      .then((results) => {
        const successfulGroups = results
          .filter((result) => result.status === 'fulfilled')
          .flatMap((result) => result.value);

        const firstError = results.find((result) => result.status === 'rejected');

        if (firstError) {
          setEstimationError(firstError.reason);
        }

        if (successfulGroups.length > 0) {
          const updatedGroups = mergeAiQuantities(intentGroups, successfulGroups);
          setEstimatedMaterials(updatedGroups);
        } else {
          setEstimatedMaterials(intentGroups);
        }
      })
      .finally(() => {
        setIsDone(true);
      });
  }, [
    isFetchingProducts,
    resolvedProductData,
    itemIdsToFetch,
    estimateQuantity,
    intentGroups,
    listTitle,
    aiPromptMessage,
    questionnaireAnswers,
    metaData,
    mcvisId
  ]);

  const estimationProgress = useMemo(() => {
    const totalProducts = itemIdsToFetch.length;
    const completedProducts = Math.min(Object.keys(resolvedProductData).length, totalProducts);
    // Product fetching = 0-25%, agent phase = 25-100% based on group completion
    const productFetchProgress = totalProducts > 0
      ? Math.round((completedProducts / totalProducts) * 25)
      : 0;
    const agentProgress = groupsTotal > 0
      ? Math.round((groupsDone / groupsTotal) * 75)
      : 0;
    const overallProgress = isDone ? 100 : productFetchProgress + agentProgress;
    return {
      completedProducts,
      totalProducts,
      overallProgress,
      isEstimating: started.current && !isDone,
      isDone,
    };
  }, [itemIdsToFetch, resolvedProductData, isDone, groupsDone, groupsTotal]);

  return {
    estimatedMaterials,
    estimationProgress,
    estimationError,
  };
};

useQuantityEstimator.dataModel = extend({
  products: params({
    itemIds: arrayType(stringType().isRequired()).isRequired(),
    dataSource: stringType(),
    loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
      svocID: stringType(),
      programTiers: arrayType(
        shapeType({
          tier: stringType(),
          program: stringType()
        })
      )
    })
  }).arrayOf(shapeType({
    itemId: stringType(),
    identifiers: shapeType({
      productLabel: stringType(),
      brandName: stringType()
    }),
    info: shapeType({
      pipCalculator: shapeType({
        coverageUnits: stringType(),
        display: boolType(),
        publisher: numberType(),
        toggle: stringType(),
        defaultAdditionalCoverage: numberType(),
        additionalCoveragePercentage: boolType()
      }),
      unitOfMeasureCoverage: stringType()
    }),
    pricing: params({
      storeId: stringType(),
      isBrandPricingPolicyCompliant: boolType()
    }).shape({
      unitOfMeasure: stringType(),
      alternate: shapeType({
        unit: shapeType({
          caseUnitOfMeasure: stringType(),
          unitsPerCase: numberType()
        })
      })
    }),
    specificationGroup: arrayType(shapeType({
      specTitle: stringType(),
      specifications: shapeType({
        itemProp: stringType(),
        specName: stringType(),
        specValue: stringType(),
        packagingHierarchyMeta: shapeType({
          depth: numberType(),
          gln: stringType(),
          gtin: stringType(),
          height: numberType(),
          hierarchyLevelCode: stringType(),
          itf14: stringType(),
          nestable: boolType(),
          packageBuyUomQty: numberType(),
          packageEachQty: numberType(),
          packageUnitQty: numberType(),
          productId: stringType(),
          sizeUom: stringType(),
          upc: stringType(),
          weight: numberType(),
          weightUom: stringType(),
          width: numberType()
        })
      })
    }))
  })),
  interactWithAgent: params({
    agent: customType('Agent').enum(['DEFAULT', 'VERTEX_AI']),
    interactionType: customType('InteractionType').enum(['MESSAGE', 'FEEDBACK', 'QUANTITY_ESTIMATION']).isRequired(),
    message: stringType(),
    template: customType('TemplateType').enum(['PROJECT_DESCRIPTION', 'MATERIALS_TO_LIST', 'QUANTITY_ESTIMATION']),
    templateData: arrayType(
      customType('TemplateData')
        .shape({
          key: stringType(),
          value: stringType()
        })
    ),
    metaData: customType('MetadataInput').shape({
      refType: stringType(),
      refId: stringType(),
      channel: stringType(),
      mcvis: stringType()
    })
  }).mutation().shape({
    metadata: shapeType({
      inputTokenCount: numberType(),
      sessionId: stringType(),
      statusMessage: stringType()
    }),
    error: stringType(),
    materialsGroups: arrayType(
      shapeType({
        groupName: stringType(),
        groupId: stringType(),
        materials: arrayType(
          shapeType({
            intent: stringType(),
            quantity: numberType(),
            calculationBasis: stringType(),
            estimateAiQuantity: boolType()
          })
        )
      })
    )
  })
});
